/**
 * Tiny shared 2-D vector helper used by the DIMENSION and MULTILEADER
 * geometry builders (`dimensionGeometry.ts` / `multileaderGeometry.ts`).
 * Extracted to ONE file so the two builders — which independently needed
 * the identical "normalize, degenerate → +X fallback" helper — don't carry
 * two copies (jscpd dup gate).
 */

/** Plain 2-D point — both bag readers and typed readers produce this. */
export interface RawVec2 {
  x: number;
  y: number;
}

/**
 * Unit vector from `(x, y)`; degenerate (near-zero magnitude) → `(1, 0)`.
 *
 * The implementation moved to `@dxf-ai-editor/cad-core` when the wide-polyline
 * BAND math did — `polylineWidthOutline.ts` is one of its callers and that
 * package may not import `apps/*` — and is re-exported here so this module
 * stays the import site the DIMENSION / MULTILEADER builders already use. One
 * implementation, not two (CLAUDE.md `dup:check` one-truth).
 */
export { unitVec2 } from "@dxf-ai-editor/cad-core";

/**
 * GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up)
 * P2 × 2 — the DIMENSION and MULTILEADER measurement-text bbox extenders
 * each hard-coded an AXIS-ALIGNED box regardless of the text's rendered
 * rotation, so a rotated (group-53 / `textRotation`) label could render
 * outside `RenderableEntity.bounds` and drop out of spatial-index culling
 * and window/pick selection. Rotate the 4 local-space corners of the text
 * box (relative to `anchor` at local `(0,0)`, BEFORE rotation) by
 * `rotationRad` and return their WORLD positions so callers extend their
 * bbox with the true rotated footprint instead of an unrotated one.
 *
 * `localMinY`/`localMaxY` let a caller encode an asymmetric box (Dimension's
 * "baseline-center" text sits ABOVE the anchor, `0`..`+height` — and a
 * MIRRTEXT=1 Dimension's local-Y-flipped glyph sits BELOW it instead,
 * `-height`..`0`, per `applyMirrorScale`'s render convention). A caller with
 * a Y-symmetric box (Multileader's MTEXT content, `-height`..`+height`)
 * passes symmetric bounds — mirroring such a box is a no-op on its extent,
 * matching why only the Dimension call site threads mirror handedness here.
 */
export function rotatedBoxCorners(
  anchor: RawVec2,
  localMinX: number,
  localMaxX: number,
  localMinY: number,
  localMaxY: number,
  rotationRad: number,
): RawVec2[] {
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  const localCorners: RawVec2[] = [
    { x: localMinX, y: localMinY },
    { x: localMaxX, y: localMinY },
    { x: localMaxX, y: localMaxY },
    { x: localMinX, y: localMaxY },
  ];
  return localCorners.map((c) => ({
    x: anchor.x + c.x * cos - c.y * sin,
    y: anchor.y + c.x * sin + c.y * cos,
  }));
}
